import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { request } from "../services/server";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import "./css/prospect-search.css";

const PAGE_SIZE = 2000;

export default function ProspectSearch() {
  const navigate = useNavigate();

  // Categories loaded from the API
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState("");

  // Filter state — keyed by category code
  const [filters, setFilters] = useState({});

  // Count / search state
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  // ── Load categories on mount ───────────────────────────────────────
  useEffect(() => {
    request("/retriever/prospect/categories/")
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => setCatError(err.message || "Kunde inte ladda filterkategorier."))
      .finally(() => setCatLoading(false));
  }, []);

  // ── Filter helpers ─────────────────────────────────────────────────
  function setFilter(code, value) {
    setFilters((prev) => ({ ...prev, [code]: value }));
    setCount(null);
    setResults([]);
    setPage(1);
  }

  function clearFilters() {
    setFilters({});
    setCount(null);
    setResults([]);
    setPage(1);
    setError("");
  }

  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v)
  );
  const hasFilters = Object.keys(activeFilters).length > 0;

  // ── Count ──────────────────────────────────────────────────────────
  const handleCount = useCallback(async () => {
    if (!hasFilters) return;
    setCounting(true);
    setError("");
    try {
      const data = await request("/retriever/prospect/count/", {
        method: "POST",
        body: { filters: activeFilters },
      });
      setCount(data.count);
    } catch (err) {
      setError(err.message || "Kunde inte räkna resultat.");
    } finally {
      setCounting(false);
    }
  }, [activeFilters, hasFilters]);

  // ── Search ─────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    async (p = 1) => {
      if (!hasFilters) return;
      setSearching(true);
      setError("");
      try {
        const data = await request("/retriever/prospect/search/", {
          method: "POST",
          body: { filters: activeFilters, page: p },
        });
        setResults(data.results || []);
        setCount(data.count);
        setPage(p);
      } catch (err) {
        setError(err.message || "Sökning misslyckades.");
      } finally {
        setSearching(false);
      }
    },
    [activeFilters, hasFilters]
  );

  const totalPages = count != null ? Math.ceil(count / PAGE_SIZE) : 0;

  const navItems = makeCompanyNav(null);

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Main navItems={navItems}>
    <div className="ps-shell">
      <div className="ps-topbar">
        <h1>Prospektering</h1>
      </div>

      <div className="ps-body">
        {/* ── Sidebar ─────────────────────────────────────────── */}
        <div className="ps-sidebar">
          {catLoading && <div className="ps-loading">Laddar filter...</div>}
          {catError && <div className="ps-error">{catError}</div>}

          {categories.map((cat) => (
            <div key={cat.code} className="ps-filter-group">
              <label>{cat.name}</label>
              <select
                value={filters[cat.code] || ""}
                onChange={(e) => setFilter(cat.code, e.target.value)}
              >
                <option value="">Alla</option>
                {(cat.values || []).map((v) => (
                  <option key={v.code} value={v.code}>
                    {v.code} — {v.text}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className="ps-btn-row">
            <button
              className="ps-btn ps-btn--secondary"
              onClick={clearFilters}
            >
              Rensa
            </button>
            <button
              className="ps-btn ps-btn--primary"
              disabled={!hasFilters || counting}
              onClick={handleCount}
            >
              {counting ? "Räknar..." : "Räkna"}
            </button>
          </div>

          {count != null && (
            <div className="ps-count-badge">
              {count.toLocaleString("sv-SE")} träffar
            </div>
          )}

          {count != null && count > 0 && (
            <div className="ps-btn-row" style={{ marginTop: 8 }}>
              <button
                className="ps-btn ps-btn--primary"
                disabled={searching}
                onClick={() => handleSearch(1)}
                style={{ flex: "1 1 100%" }}
              >
                {searching ? "Söker..." : "Visa resultat"}
              </button>
            </div>
          )}
        </div>

        {/* ── Results ─────────────────────────────────────────── */}
        <div className="ps-results">
          {error && <div className="ps-error">{error}</div>}

          {results.length === 0 && !searching && (
            <div className="ps-empty">
              Välj filter och klicka "Räkna" för att börja prospektera.
            </div>
          )}

          {searching && <div className="ps-loading">Söker...</div>}

          {results.length > 0 && (
            <>
              <div className="ps-results-header">
                <h2>Resultat</h2>
                <span className="ps-results-count">
                  Visar {results.length} av {count?.toLocaleString("sv-SE")}
                  {totalPages > 1 && ` (sida ${page} av ${totalPages})`}
                </span>
              </div>

              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Org.nr</th>
                    <th>Namn</th>
                    <th>SNI</th>
                    <th>Kommun</th>
                    <th>Län</th>
                    <th>Juridisk form</th>
                    <th>Anställda</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.org_number || i}>
                      <td>{r.org_number}</td>
                      <td>{r.name}</td>
                      <td title={r.sni_text}>{r.sni_code}</td>
                      <td>{r.municipality}</td>
                      <td>{r.county}</td>
                      <td>{r.legal_form}</td>
                      <td>{r.num_employees}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="ps-pagination">
                  <button
                    disabled={page <= 1 || searching}
                    onClick={() => handleSearch(page - 1)}
                  >
                    Föregående
                  </button>
                  <span>
                    Sida {page} av {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages || searching}
                    onClick={() => handleSearch(page + 1)}
                  >
                    Nästa
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </Main>
  );
}
